// NOTE: Composed entities selector which is not fit to the feature slice.
import { createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash-es';

import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { FEATURE_FLAG_ENABLE_SCRIBE_FEATURE } from '@/shared/config';

import { albumModel } from '@/entities/album';
import { allowedToothConditionsModel } from '@/entities/allowedToothConditions';
import { assetsModel, getAssetMediaKind } from '@/entities/assets';
import {
  billingModel,
  DiagnocoinFunding,
  DiagnocoinFundingByOrderReportType,
} from '@/entities/billing';
import type { CompactCondition } from '@/entities/condition';
import {
  CHILD_CONDITION_CODES_BY_PARENT_CODE,
  conditionModel,
  FDA_ONLY_CONDITION_CODES,
  FLAT_CONDITION_GROUP_CODES,
  getFDAVersion2ConditionCodes,
  getToothStatus,
  isUserPositiveCertainty,
  LogicalCondition,
  makeCompactConditions,
  processLogicalConditions,
  splitConditionCodes,
  splitConditions,
} from '@/entities/condition';
import {
  BASE_STORAGE_LIMIT,
  BYTES_PER_GB,
  organizationModel,
  STORAGE_FULL_THRESHOLD,
  STORAGE_WARNING_THRESHOLD,
} from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { isReport2D, reportsModel } from '@/entities/reports';
import {
  DELETED_TOOTH_DETECTION_ISO_NUMBER,
  toothModel,
} from '@/entities/tooth';

import type { InviteColleagueCardState, ToothSliceContext } from '../config';
import {
  findDCCTVolumeFileID,
  isDeleted,
  isMissingConditionForTooth,
  isToothSliceAsset,
} from '../lib';

const selectCanViewPatientArchive = (
  patientID: string,
  permission: 'CanViewArchivedReports' | 'CanViewArchivedEncounters',
) =>
  createSelector(
    patientModel.selectors.selectPatientByID(patientID),
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ArchiveReports,
    ),
    (patient, archiveEnabled) =>
      Boolean(archiveEnabled && patient?.YourPermissions?.[permission]),
  );

export const selectCanViewPatientReportsArchive = (patientID: string) =>
  selectCanViewPatientArchive(patientID, 'CanViewArchivedReports');

export const selectCanViewPatientRecordingsArchive = (patientID: string) =>
  selectCanViewPatientArchive(patientID, 'CanViewArchivedEncounters');

const collectUserPositiveConditionIDs = (conditions: LogicalCondition[]) => {
  const ids = new Set<string>();

  const collect = (condition: LogicalCondition) => {
    if (isUserPositiveCertainty(condition.Certainty)) {
      [condition.ID, ...condition.IDs].filter(Boolean).forEach((id) => {
        ids.add(id);
      });
    }

    condition.Children.forEach(collect);
  };

  conditions.forEach(collect);

  return ids;
};

const applyUnaidedManualDecisionState =
  (userPositiveConditionIDs: Set<string>) =>
  (condition: CompactCondition): CompactCondition => {
    const isUserPositive = [condition.id, ...condition.ids]
      .filter(Boolean)
      .some((id) => userPositiveConditionIDs.has(id));

    return {
      ...condition,
      isChecked: isUserPositive,
      modelPositive: false,
      childConditionInterfaces: condition.childConditionInterfaces.map(
        applyUnaidedManualDecisionState(userPositiveConditionIDs),
      ),
    };
  };

const filterCompactConditionsByIDs =
  (conditionIDs: Set<string>) =>
  (condition: CompactCondition): CompactCondition | null => {
    const childConditionInterfaces = condition.childConditionInterfaces
      .map(filterCompactConditionsByIDs(conditionIDs))
      .filter((childCondition): childCondition is CompactCondition =>
        Boolean(childCondition),
      );

    const isIncluded = [condition.id, ...condition.ids]
      .filter(Boolean)
      .some((id) => conditionIDs.has(id));

    if (!isIncluded && childConditionInterfaces.length === 0) {
      return null;
    }

    return {
      ...condition,
      childConditionInterfaces,
    };
  };

const filterCompactConditionsByCodes =
  (allowedCodes: Set<ConditionCode>) =>
  (condition: CompactCondition): CompactCondition | null => {
    const childConditionInterfaces = condition.childConditionInterfaces
      .map(filterCompactConditionsByCodes(allowedCodes))
      .filter((childCondition): childCondition is CompactCondition =>
        Boolean(childCondition),
      );

    if (
      !allowedCodes.has(condition.code) &&
      childConditionInterfaces.length === 0
    ) {
      return null;
    }

    return {
      ...condition,
      childConditionInterfaces,
    };
  };

// Filter out FDA-only codes recursively (for non-FDA mode)
const filterOutFDAOnlyConditions =
  (excludedCodes: Set<ConditionCode>) =>
  (condition: CompactCondition): CompactCondition | null => {
    // If this condition is FDA-only, exclude it
    if (excludedCodes.has(condition.code)) {
      return null;
    }

    // Recursively filter children
    const childConditionInterfaces = condition.childConditionInterfaces
      .map(filterOutFDAOnlyConditions(excludedCodes))
      .filter((childCondition): childCondition is CompactCondition =>
        Boolean(childCondition),
      );

    return {
      ...condition,
      childConditionInterfaces,
    };
  };

const makeAllowedConditions = (
  toothID: string,
  toothConditions: Condition[],
  conditionCodes?: ConditionCode[],
) => {
  // Collection of all possible conditions for the tooth
  const allowedToothConditions: Condition[] = [];

  // Split allowed tooth condition codes by parent and children codes
  const {
    parentCodes: allowedParentCodes,
    childrenCodes: allowedChildrenCodes,
  } = splitConditionCodes(conditionCodes);

  // Split existed tooth conditions by parent and children conditions
  const { conditions, childrenConditions } = splitConditions(toothConditions);

  // Filtered parent condition codes by allowed parent conditions code for the tooth
  const allowedParentConditionCodes = FLAT_CONDITION_GROUP_CODES.filter(
    (parentCode) => allowedParentCodes.includes(parentCode),
  );

  // Iterate allowedParentConditionCodes to include all existing allowed tooth parent conditions and
  // add other possible allowed condition for the possbility to add them by user.
  // NOTE: Spead operator because conditions grouping by code to collect duplicates in one place
  allowedParentConditionCodes.forEach((code) => {
    if (code in conditions) {
      allowedToothConditions.push(...conditions[code]);
    } else {
      const emptyCondition = new Condition({
        Attribution: { case: 'Tooth', value: { ToothID: toothID } },
        Code: code,
      });

      allowedToothConditions.push(emptyCondition);
    }

    if (code in CHILD_CONDITION_CODES_BY_PARENT_CODE) {
      const groupedChildrenConditions = groupBy(
        childrenConditions[code],
        'Code',
      );
      // WARN: I don't know if it's necessary step.
      //       Is there a case when condition should contain only part of children conditions?
      //
      // const allowedChildrenConditionCodes = allowedChildrenCodes;
      const allowedChildrenConditionCodes =
        CHILD_CONDITION_CODES_BY_PARENT_CODE[code].filter((childrenCode) =>
          allowedChildrenCodes.includes(childrenCode),
        );

      // Iterate allowedChildrenConditionCodes to include all existing allowed tooth children conditions and
      // add other possible allowed conditions for the possbility to add them by user.
      // NOTE: Spead operator because conditions grouping by code to collect duplicates in one place
      allowedChildrenConditionCodes.forEach((childrenCode) => {
        if (childrenCode in groupedChildrenConditions) {
          allowedToothConditions.push(
            ...groupedChildrenConditions[childrenCode],
          );
        } else {
          const emptyChildrenCondition = new Condition({
            Attribution: { case: 'Tooth', value: { ToothID: toothID } },
            Code: childrenCode,
            ParentCode: code,
            ParentID: code in conditions ? conditions[code][0]?.ID : undefined,
          });

          allowedToothConditions.push(emptyChildrenCondition);
        }
      });
    }
  });

  return allowedToothConditions;
};

export const selectToothStatus = (
  toothID: string,
  ignoreLowProbability = false,
) =>
  createSelector(
    conditionModel.selectors.selectByToothID(toothID),
    reportsModel.selectors.selectLowProbabilityMode,
    reportsModel.selectors.selectCurrentReportType,
    organizationModel.selectors.selectFDAEnvironment,
    (
      toothConditions,
      lowProbabilityMode,
      currentReportType,
      fdaEnvironment,
    ) => {
      const isGPReport = currentReportType === ReportType.ReportType_CBCT_GP;
      const isFDA3DGPRestrictions =
        fdaEnvironment.isFDA3DGPRestrictions && isGPReport;

      return getToothStatus(toothConditions, {
        showLowProbability: lowProbabilityMode,
        isFDA: fdaEnvironment.isFDA,
        isFDANonAided: fdaEnvironment.treatToothLikeAsTooth,
        ignoreLowProbability,
        isFDA3DGPRestrictions,
        isFDAVersion2: fdaEnvironment.isFDAVersion2,
        is2DReport: isReport2D(currentReportType),
      });
    },
  );

// NOTE: This selector collects all allowed tooth conditions received from the backend
// in combination with all other allowed conditions for the tooth in its current state,
// which is regulating by condition engine on the backend (allowedToothConditionsHint).
export const selectAllowedConditions = (toothID: string) =>
  createSelector(
    conditionModel.selectors.selectByToothID(toothID),
    allowedToothConditionsModel.selectors.selectById(toothID),
    (toothConditions, allowedToothConditionsHint) => {
      return makeAllowedConditions(
        toothID,
        toothConditions,
        allowedToothConditionsHint?.ConditionCodes,
      );
    },
  );

export const selectAllowedLogicalConditions = (toothID: string) =>
  createSelector(selectAllowedConditions(toothID), (conditions) =>
    processLogicalConditions(conditions),
  );

export const selectToothActiveLogicalConditions = (toothID: string) =>
  createSelector(
    conditionModel.selectors.selectToothActiveLogicalConditions(toothID),
    conditionModel.selectors.selectToothActiveLogicalConditionsForCBCTGP(
      toothID,
    ),
    conditionModel.selectors.selectToothActiveLogicalConditionsForFDAVersion2(
      toothID,
    ),
    conditionModel.selectors.selectToothActiveLogicalConditionsForCBCTGPAndFDAVersion2(
      toothID,
    ),
    reportsModel.selectors.selectCurrentReport,
    organizationModel.selectors.selectFDAEnvironment,
    (
      activeLogicalConditions,
      activeLogicalConditionsForCBCTGP,
      activeLogicalConditionsForFDAVersion2,
      activeLogicalConditionsForCBCTGPAndFDAVersion2,
      currentReport,
      fdaEnvironment,
    ): LogicalCondition[] => {
      const isGPReport = currentReport?.Type === ReportType.ReportType_CBCT_GP;
      const isFDAVersion2 = fdaEnvironment.manualPercentInput;

      // FDA 2.0: use selectors that also include UserDecision/UserScore conditions
      // (manual annotations by annotators)
      const conditionKey = `${isFDAVersion2}_${isGPReport}`;

      const conditionsMap = {
        true_true: activeLogicalConditionsForCBCTGPAndFDAVersion2,
        true_false: activeLogicalConditionsForFDAVersion2,
        false_true: activeLogicalConditionsForCBCTGP,
        false_false: activeLogicalConditions,
      };

      return conditionsMap[conditionKey as keyof typeof conditionsMap];
    },
  );

export const selectCompactToothConditions = (toothID: string) =>
  createSelector(
    selectToothActiveLogicalConditions(toothID),
    reportsModel.selectors.selectCurrentReport,
    reportsModel.selectors.selectLowProbabilityMode,
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
    organizationModel.selectors.selectFDAEnvironment,
    toothModel.selectors.selectISONumberByID(toothID),
    (
      conditions,
      currentReport,
      lowProbabilityMode,
      hideProbabilities,
      fdaEnvironment,
      ISONumber,
    ): CompactCondition[] => {
      const isGPReport = currentReport?.Type === ReportType.ReportType_CBCT_GP;

      // FDA 2.0 annotator enters the percent manually — never show the model score
      const shouldHideProbabilities =
        hideProbabilities ||
        (fdaEnvironment.isFDA3DGPRestrictions && isGPReport) ||
        fdaEnvironment.manualPercentInput;

      const toothISONumber = ISONumber ?? 0;

      return makeCompactConditions(
        conditions,
        toothISONumber,
        lowProbabilityMode,
        shouldHideProbabilities,
      );
    },
  );

export const selectPreviewCompactToothConditions = (toothID: string) =>
  createSelector(
    conditionModel.selectors.selectToothPreviewActiveLogicalConditions(toothID),
    conditionModel.selectors.selectToothPreviewActiveLogicalConditionsForCBCTGP(
      toothID,
    ),
    reportsModel.selectors.selectCurrentReport,
    reportsModel.selectors.selectLowProbabilityMode,
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
    organizationModel.selectors.selectFDAEnvironment,
    toothModel.selectors.selectISONumberByID(toothID),
    (
      conditions,
      conditionsForCBCTGP,
      currentReport,
      showLowProbability,
      hideProbabilities,
      fdaEnvironment,
      ISONumber,
    ): CompactCondition[] => {
      const isGPReport = currentReport?.Type === ReportType.ReportType_CBCT_GP;
      const filteredConditions = isGPReport ? conditionsForCBCTGP : conditions;

      const shouldHideProbabilities =
        hideProbabilities ||
        (fdaEnvironment.isFDA3DGPRestrictions && isGPReport) ||
        fdaEnvironment.manualPercentInput;

      const toothISONumber = ISONumber ?? 0;

      // The tooth anatomy condition duplicates the tooth title next to it
      // in the print preview, so it is excluded from the compact list (WA-6228)
      const compactConditions = makeCompactConditions(
        filteredConditions,
        toothISONumber,
        showLowProbability,
        shouldHideProbabilities,
      ).filter((condition) => condition.code !== ConditionCode.CodeTooth);

      const allowedCompactConditions = fdaEnvironment.isFDAVersion2
        ? compactConditions
            .map(
              filterCompactConditionsByCodes(
                new Set(
                  getFDAVersion2ConditionCodes(isReport2D(currentReport?.Type)),
                ),
              ),
            )
            .filter((condition): condition is CompactCondition =>
              Boolean(condition),
            )
        : compactConditions
            .map(filterOutFDAOnlyConditions(new Set(FDA_ONLY_CONDITION_CODES)))
            .filter((condition): condition is CompactCondition =>
              Boolean(condition),
            );

      if (fdaEnvironment.isUnaided) {
        const userPositiveConditionIDs =
          collectUserPositiveConditionIDs(filteredConditions);

        return allowedCompactConditions
          .map(filterCompactConditionsByIDs(userPositiveConditionIDs))
          .filter((condition): condition is CompactCondition =>
            Boolean(condition),
          );
      }

      return allowedCompactConditions;
    },
  );

export const selectAllowedCompactToothConditions = (toothID: string) =>
  createSelector(
    conditionModel.selectors.selectByToothID(toothID),
    allowedToothConditionsModel.selectors.selectById(toothID),
    reportsModel.selectors.selectLowProbabilityMode,
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Hide_Probabilities,
    ),
    organizationModel.selectors.selectFDAEnvironment,
    reportsModel.selectors.selectCurrentReport,
    toothModel.selectors.selectISONumberByID(toothID),
    (
      toothConditions,
      allowedToothConditionsHint,
      lowProbabilityMode,
      hideProbabilities,
      fdaEnvironment,
      currentReport,
      ISONumber,
    ): CompactCondition[] => {
      const isGPReport = currentReport?.Type === ReportType.ReportType_CBCT_GP;
      const shouldHideProbabilities =
        hideProbabilities ||
        (fdaEnvironment.isFDA3DGPRestrictions && isGPReport) ||
        fdaEnvironment.manualPercentInput;

      const toothISONumber = ISONumber ?? 0;
      const conditions = processLogicalConditions(
        makeAllowedConditions(
          toothID,
          toothConditions,
          allowedToothConditionsHint?.ConditionCodes,
        ),
      );

      const compactConditions = makeCompactConditions(
        conditions,
        toothISONumber,
        lowProbabilityMode,
        shouldHideProbabilities,
      );

      const allowedCompactConditions = fdaEnvironment.isFDAVersion2
        ? compactConditions
            .map(
              filterCompactConditionsByCodes(
                new Set(
                  getFDAVersion2ConditionCodes(isReport2D(currentReport?.Type)),
                ),
              ),
            )
            .filter((condition): condition is CompactCondition =>
              Boolean(condition),
            )
        : compactConditions
            .map(filterOutFDAOnlyConditions(new Set(FDA_ONLY_CONDITION_CODES)))
            .filter((condition): condition is CompactCondition =>
              Boolean(condition),
            );

      if (fdaEnvironment.isUnaided) {
        const userPositiveConditionIDs =
          collectUserPositiveConditionIDs(conditions);

        return allowedCompactConditions.map(
          applyUnaidedManualDecisionState(userPositiveConditionIDs),
        );
      }

      return allowedCompactConditions;
    },
  );

export const selectPurchasedStorageLimit = createSelector(
  billingModel.selectors.selectServicesByType(
    ServiceType.ServiceType_Expand_Storage,
  ),
  (storageServices) =>
    storageServices.reduce((total, service) => {
      if (service.Kind.case === 'Countable') {
        return total + service.Kind.value.Limit * BYTES_PER_GB;
      }

      return total;
    }, BigInt(0)),
);

export const selectTotalStorageLimit = createSelector(
  selectPurchasedStorageLimit,
  (purchasedStorage) => BASE_STORAGE_LIMIT + purchasedStorage,
);

export const selectStoragePercentage = createSelector(
  organizationModel.selectors.selectStorageUsed,
  selectTotalStorageLimit,
  (used, limit) => {
    if (limit === BigInt(0)) {
      return 0;
    }

    return Number((used * BigInt(100)) / limit);
  },
);

export const selectAlbumsWithVideosRemoved = createSelector(
  albumModel.selectors.selectAlbumsWithAssets,
  assetsModel.selectors.selectEntities,
  (albums, assetsEntities) =>
    albums
      .map((album) => ({
        ...album,
        StudyAssetIDs: album.StudyAssetIDs.filter(
          (assetID) => getAssetMediaKind(assetsEntities[assetID]) === 'photo',
        ),
      }))
      .filter((album) => album.StudyAssetIDs.length > 0),
);

export const selectIsStorageWarning = createSelector(
  selectStoragePercentage,
  (percentage) =>
    percentage >= STORAGE_WARNING_THRESHOLD &&
    percentage < STORAGE_FULL_THRESHOLD,
);

export const selectIsStorageFull = createSelector(
  selectStoragePercentage,
  (percentage) => percentage >= STORAGE_FULL_THRESHOLD,
);

export const selectIsStorageFeatureReady = createSelector(
  organizationModel.selectors.selectFeatureFlag(
    FeatureFlag.FeatureFlag_Enable_StorageFeature,
  ),
  billingModel.selectors.selectIsBillingLoaded,
  (isStorageFeatureEnabled, isBillingLoaded) =>
    isStorageFeatureEnabled && isBillingLoaded,
);

export const selectIsStorageBlocked = createSelector(
  selectIsStorageFeatureReady,
  selectIsStorageFull,
  (isStorageFeatureReady, isStorageFull) =>
    isStorageFeatureReady && isStorageFull,
);

export const selectToothSliceContext = (toothID: string) =>
  createSelector(
    toothModel.selectors.selectToothByID(toothID),
    assetsModel.selectors.selectAll,
    assetsModel.selectors.selectPatientAssetsSnapshot,
    assetsModel.selectors.selectReportAssetsSnapshot,
    conditionModel.selectors.selectByToothID(toothID),
    conditionModel.selectors.selectReportConditionsSnapshot,
    reportsModel.selectors.selectCurrentReport,
    (
      tooth,
      assets,
      patientAssetsSnapshot,
      reportAssetsSnapshot,
      toothConditions,
      reportConditionsSnapshot,
      report,
    ): ToothSliceContext | null => {
      if (
        !report ||
        isDeleted(report) ||
        !tooth ||
        tooth.ID !== toothID ||
        tooth.IsRemoved ||
        tooth.Numeration?.ISO === DELETED_TOOTH_DETECTION_ISO_NUMBER ||
        isDeleted(tooth) ||
        tooth.ReportID !== report.ID ||
        tooth.PatientID !== report.PatientID ||
        tooth.OrganizationID !== report.OrganizationID
      ) {
        return null;
      }

      const toothAssets = assets.filter((asset) =>
        isToothSliceAsset({ asset, report, tooth }),
      );
      const missingCondition =
        toothConditions.find((condition) =>
          isMissingConditionForTooth({ condition, report, tooth }),
        ) ?? null;

      return {
        missingCondition,
        report,
        snapshots: {
          patientAssets: patientAssetsSnapshot,
          reportAssets: reportAssetsSnapshot,
          reportConditions: reportConditionsSnapshot,
        },
        tooth,
        toothAssets,
        volumeFileID: findDCCTVolumeFileID(assets, report),
      };
    },
  );
export const selectIsDiagnocoinsEnabled = createSelector(
  organizationModel.selectors.selectFeatureFlag(
    FeatureFlag.FeatureFlag_Enable_Diagnocoins,
  ),
  billingModel.selectors.selectIsDiagnocoinReady,
  (isFeatureEnabled, isReady) => isFeatureEnabled && isReady,
);

export const selectInviteColleagueCardState = createSelector(
  organizationModel.selectors.selectCurrentOrganization,
  organizationModel.selectors.selectFeatureList,
  billingModel.selectors.selectDiagnocoinLoading,
  billingModel.selectors.selectReferralRewards,
  (
    organization,
    features,
    diagnocoinLoading,
    rewards,
  ): InviteColleagueCardState => {
    const canViewCoinAmounts = Boolean(
      organization?.YourPermissions?.BillingCanViewInventory,
    );

    return {
      isVisible:
        Boolean(organization?.ID) &&
        Boolean(features[FeatureFlag.FeatureFlag_Enable_Diagnocoins]) &&
        Boolean(features[FeatureFlag.FeatureFlag_Enable_ReferralProgram]),
      isLoading:
        canViewCoinAmounts &&
        (diagnocoinLoading === 'idle' || diagnocoinLoading === 'pending'),
      rewards: canViewCoinAmounts ? rewards : null,
    };
  },
);

const applyDiagnocoinsFlag = (
  funding: DiagnocoinFunding,
  isEnabled: boolean,
): DiagnocoinFunding =>
  isEnabled || funding.source !== 'diagnocoins'
    ? funding
    : { source: 'unavailable' };

export const makeSelectDiagnocoinFunding = (
  serviceTypes: readonly ServiceType[],
) =>
  createSelector(
    selectIsDiagnocoinsEnabled,
    billingModel.selectors.makeSelectDiagnocoinFundingForServiceTypes(
      serviceTypes,
    ),
    (isEnabled, funding) => applyDiagnocoinsFlag(funding, isEnabled),
  );

export const selectDiagnocoinFundingByOrderReportType = createSelector(
  selectIsDiagnocoinsEnabled,
  billingModel.selectors.selectDiagnocoinFundingByOrderReportType,
  (isEnabled, fundingByReportType): DiagnocoinFundingByOrderReportType =>
    Object.fromEntries(
      Object.entries(fundingByReportType).map(([reportType, funding]) => [
        reportType,
        applyDiagnocoinsFlag(funding, isEnabled),
      ]),
    ) as DiagnocoinFundingByOrderReportType,
);

export const selectTranscriptorAvailability = createSelector(
  organizationModel.selectors.selectFDAEnvironment,
  organizationModel.selectors.selectFeatureFlag(
    FEATURE_FLAG_ENABLE_SCRIBE_FEATURE,
  ),
  billingModel.selectors.selectActiveServiceValuesByTypes([
    ServiceType.ServiceType_Order_Transcriptor,
    ServiceType.ServiceType_Enable_Transcriptor,
  ]),
  selectDiagnocoinFundingByOrderReportType,
  billingModel.selectors.selectIsBillingLoaded,
  organizationModel.selectors.selectFeatureFlag(
    FeatureFlag.FeatureFlag_Enable_Diagnocoins,
  ),
  billingModel.selectors.selectDiagnocoinLoading,
  (
    { isFDAVersion2 },
    enableScribeFeature,
    services,
    { Transcriptor: funding },
    isBillingLoaded,
    isDiagnocoinsEnabled,
    diagnocoinLoading,
  ) => {
    const hasFunding =
      funding.source === 'included' || funding.source === 'diagnocoins';
    // Visibility mirrors the other order buttons: a subscription that carries
    // the Transcriptor service keeps the button on screen even at 0 leftover
    const isAvailable =
      !isFDAVersion2 &&
      (enableScribeFeature ||
        services[ServiceType.ServiceType_Enable_Transcriptor] !== undefined ||
        services[ServiceType.ServiceType_Order_Transcriptor] !== undefined ||
        hasFunding);

    return {
      funding,
      isAvailable,
      // Scribe consumes Order_Transcriptor (leftover or coins) for every
      // encounter regardless of the feature flag or the legacy enabler, so an
      // order is possible only while that funding exists
      canOrder: isAvailable && hasFunding,
      isReady:
        isBillingLoaded &&
        (isAvailable ||
          isFDAVersion2 ||
          !isDiagnocoinsEnabled ||
          diagnocoinLoading === 'succeeded' ||
          diagnocoinLoading === 'failed'),
    };
  },
);
